import { matchLevels } from '../Lib/redux/sagas/getCustomerMatches/matchLevels';

/**
 * Filters out reseller suppliers from a matches array.
 * Resellers are identified by isReseller: true from the gateway response.
 */
export const filterResellers = (matches) =>
  matches.filter((match) => !match.isReseller);

/**
 * Keeps only brands explicitly flagged as exclusive. The check is strict
 * (`isExclusive === true`) so brands lacking the flag — for example, the
 * current Capture-side subcategory fallback payload — are dropped until
 * the upstream data source sets the flag.
 */
export const filterNonExclusive = (matches) =>
  matches.filter((match) => match.isExclusive === true);

/**
 * Caps matches at targetCount, or tops them up with subcategory fallback
 * brands when they fall short. Fallbacks that duplicate an existing match
 * (by supplierId) are skipped, and each fallback entry is tagged with
 * matchLevel: SUBCATEGORY_REVENUE_FALLBACK so every dynamicMatches entry
 * carries provenance under the same field name.
 */
export const topUpWithFallbacks = ({ matches, fallbacks, targetCount }) => {
  if (matches.length >= targetCount) return matches.slice(0, targetCount);

  const existingIds = new Set(matches.map((m) => m.supplierId));
  const slotsToFill = targetCount - matches.length;

  const uniqueFallbacks = fallbacks
    .filter((fallback) => {
      if (existingIds.has(fallback.supplierId)) return false;
      existingIds.add(fallback.supplierId);
      return true;
    })
    .slice(0, slotsToFill)
    .map((fallback) => ({
      supplierId: fallback.supplierId,
      brandId: fallback.brandId,
      brandLabel: fallback.brandLabel,
      brandLogoUrl: fallback.brandLogoUrl,
      matchLevel: matchLevels.SUBCATEGORY_REVENUE_FALLBACK,
    }));

  return [...matches, ...uniqueFallbacks];
};

/**
 * Builds the ordered dynamicMatches list for the Select and Connect feature.
 * Non-exclusive brands are dropped from both the gateway response and the
 * subcategory fallback pool first, then non-reseller matches come first
 * (topped up with subcategory fallbacks when they fall short of targetCount),
 * then reseller matches last so resellers always appear at the bottom of the
 * Select and Connect page — below the fallbacks (GOLD-2294). The result is
 * capped at targetCount, which drops resellers before any non-reseller match.
 */
export const buildDynamicMatches = ({
  customerMatches,
  fallbacks,
  targetCount,
}) => {
  const exclusiveCustomerMatches = filterNonExclusive(customerMatches);
  const exclusiveFallbacks = filterNonExclusive(fallbacks);

  const nonResellerMatches = filterResellers(exclusiveCustomerMatches);
  const resellerMatches = exclusiveCustomerMatches.filter(
    (match) => match.isReseller
  );

  const matchesWithFallbacks = topUpWithFallbacks({
    matches: nonResellerMatches,
    fallbacks: exclusiveFallbacks,
    targetCount,
  });

  return [...matchesWithFallbacks, ...resellerMatches].slice(0, targetCount);
};
